import React from 'react';
import {
  Autocomplete,
  Box,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import { useQuery } from '@tanstack/react-query';
import apiService from '../../services/api';
import type { DashboardCityOption, DashboardGranularity } from '../../types/dashboardStats';
import { DASHBOARD_PRESETS, type PeriodMode } from '../../utils/dashboardChartUtils';

type Props = {
  granularity: DashboardGranularity;
  onGranularityChange: (value: DashboardGranularity) => void;
  periodMode: PeriodMode;
  onPeriodModeChange: (mode: PeriodMode) => void;
  presetKey: string;
  onPresetKeyChange: (key: string) => void;
  customFrom: Dayjs | null;
  onCustomFromChange: (value: Dayjs | null) => void;
  customTo: Dayjs | null;
  onCustomToChange: (value: Dayjs | null) => void;
  selectedCity: DashboardCityOption | null;
  onSelectedCityChange: (value: DashboardCityOption | null) => void;
  customRangeInvalid: boolean;
};

const DashboardPeriodFilters: React.FC<Props> = (props) => {
  const {
    granularity,
    onGranularityChange,
    periodMode,
    onPeriodModeChange,
    presetKey,
    onPresetKeyChange,
    customFrom,
    onCustomFromChange,
    customTo,
    onCustomToChange,
    selectedCity,
    onSelectedCityChange,
    customRangeInvalid,
  } = props;

  const { data: citiesData, isLoading: citiesLoading } = useQuery({
    queryKey: ['dashboard-cities'],
    queryFn: () => apiService.getDashboardCities(),
  });

  const cityOptions = citiesData?.cities ?? [];

  return (
    <Stack spacing={2}>
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>
          Ville
        </Typography>
        <Autocomplete
          size="small"
          sx={{ minWidth: { xs: '100%', sm: 280 }, maxWidth: 420 }}
          options={cityOptions}
          loading={citiesLoading}
          value={selectedCity}
          onChange={(_, value) => onSelectedCityChange(value)}
          getOptionLabel={(option) => option.city}
          isOptionEqualToValue={(option, value) => option.city === value.city}
          renderInput={(params) => (
            <TextField {...params} label="Filtrer par ville" placeholder="Toutes les villes" />
          )}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.city}>
              <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
                <span>{option.city}</span>
                <Typography variant="caption" color="text.secondary">
                  {option.count.toLocaleString('fr-FR')}
                </Typography>
              </Box>
            </Box>
          )}
        />
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>
          Granularité
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={granularity}
          onChange={(_, value: DashboardGranularity | null) => value && onGranularityChange(value)}
        >
          <ToggleButton value="hour">Heures</ToggleButton>
          <ToggleButton value="day">Jours</ToggleButton>
          <ToggleButton value="week">Semaines</ToggleButton>
          <ToggleButton value="month">Mois</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 72 }}>
          Période
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={periodMode === 'custom' ? 'custom' : presetKey}
          onChange={(_, value) => {
            if (!value) return;
            if (value === 'custom') {
              onPeriodModeChange('custom');
              return;
            }
            onPeriodModeChange('preset');
            onPresetKeyChange(value);
          }}
        >
          {DASHBOARD_PRESETS[granularity].map((preset) => (
            <ToggleButton key={preset.key} value={preset.key}>
              {preset.label}
            </ToggleButton>
          ))}
          <ToggleButton value="custom">Personnalisée</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {periodMode === 'custom' && (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <DatePicker
              label="Du"
              value={customFrom}
              onChange={onCustomFromChange}
              slotProps={{ textField: { size: 'small' } }}
              maxDate={customTo ?? undefined}
            />
            <DatePicker
              label="Au"
              value={customTo}
              onChange={onCustomToChange}
              slotProps={{ textField: { size: 'small' } }}
              minDate={customFrom ?? undefined}
              maxDate={dayjs()}
            />
            {customRangeInvalid && (
              <Typography variant="caption" color="error">
                La date de début doit être antérieure à la date de fin.
              </Typography>
            )}
          </Stack>
        </LocalizationProvider>
      )}
    </Stack>
  );
};

export default DashboardPeriodFilters;
